import { Injectable } from '@angular/core';
import { Insurance, InsuranceMode } from '@aid/insurances/types/classes';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, forkJoin, Observable, of, throwError } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { Damage } from '@aid/damages/types/classes';
import { Email } from '@aid/insurances/types/classes/email.class';
import { CrudService } from '@aid/shared/services';
import { Params } from '@angular/router';
import { InsuranceType } from '@aid/administration/types/classes';
import { PaginationResponse } from '@aid/shared/types/interfaces';
import { defined } from '@aid/shared/operators/defined';

interface InsuranceUploadFiles {
  policy?: File;
  application?: File;
}

@Injectable({
  providedIn: 'root'
})
export class InsurancesService extends CrudService<Insurance> {
  private insurance = new BehaviorSubject<Insurance | null>(null);
  private insuranceId = new BehaviorSubject<number | null>(null);
  private customerId?: number | null;

  constructor(protected http: HttpClient) {
    super(http, false);
  }

  get url(): string {
    return `customers/${this.customerId}/insurances`;
  }

  get insuranceUrl(): string {
    return `customers/${this.customerId}/insurances/${this.getInsuranceId()}`;
  }

  list(params?: Params): Observable<Insurance[]> {
    return super.list(params).pipe(
      tap(values =>
        values.sort((a, b) => {
          const first = (a.insuranceType as InsuranceType).name;
          const second = (b.insuranceType as InsuranceType).name;
          return first !== undefined && second !== undefined && first > second
            ? 1
            : -1;
        })
      )
    );
  }

  setCustomer(customerId: number | null | undefined) {
    this.customerId = customerId;
  }

  getCustomerId() {
    return this.customerId;
  }

  add(
    insurance: Insurance,
    policy?: File,
    application?: File,
    onPersisted?: (insurance: Insurance) => void
  ): Observable<Insurance> {
    return super.add(insurance).pipe(
      tap(_insurance => onPersisted?.(_insurance)),
      map(_insurance =>
        this.createFileObjects(_insurance, policy, application)
      ),
      switchMap(value =>
        this.uploadFiles(value.insurance, value.files).pipe(
          map(() => value.insurance)
        )
      )
    );
  }

  edit(
    insurance: Insurance,
    policy?: File,
    application?: File
  ): Observable<Insurance> {
    return super.edit(insurance).pipe(
      map(_insurance =>
        this.createFileObjects(_insurance, policy, application)
      ),
      switchMap(value =>
        this.uploadFiles(value.insurance, value.files).pipe(
          map(() => value.insurance)
        )
      )
    );
  }

  private createFileObjects(
    insurance: Insurance,
    policy?: File,
    application?: File
  ): { insurance: Insurance; files: InsuranceUploadFiles | null } {
    if (!policy && !application) {
      return { insurance, files: null };
    }

    const files: InsuranceUploadFiles = {};
    if (policy) {
      files.policy = policy;
    }

    if (application) {
      files.application = application;
    }

    return { insurance, files };
  }

  getBasic(valueId: number): Observable<Insurance> {
    return this.http.get<Insurance>(`${this.url}/${valueId}/basic`);
  }

  // The cached stream skips the cleared state set on route exit; getInsurance$()
  // exposes it.
  get(valueId: number): Observable<Insurance> {
    if (this.insurance.value && this.insurance.value.id === valueId) {
      return this.insurance.pipe(defined());
    }

    return super
      .get(valueId, true)
      .pipe(tap((insurance: Insurance) => this.insurance.next(insurance)));
  }

  setInsuranceId(insuranceId: number | null) {
    return this.insuranceId.next(insuranceId);
  }

  getInsuranceId$() {
    return this.insuranceId.asObservable();
  }

  getInsuranceId() {
    return this.insuranceId.value;
  }

  requireInsuranceId(): number {
    const insuranceId = this.insuranceId.value;
    if (insuranceId === null) {
      throw new Error('No insurance is selected.');
    }
    return insuranceId;
  }

  getInsurance$(): Observable<Insurance | null> {
    return this.insurance.asObservable();
  }

  getInsurance(): Insurance | null {
    return this.insurance.getValue();
  }

  setInsurance$(insurance: Insurance | null) {
    this.insurance.next(insurance);
  }

  viewFile(insurance: Insurance, type: string) {
    const url = `${this.url}/${insurance.id}/${type}`;
    return this.http.get<string>(url).subscribe(value => {
      window.open(value);
    });
  }

  uploadFiles(
    insurance: Insurance,
    files: InsuranceUploadFiles | null
  ): Observable<unknown[]> {
    if (!files) {
      return of([]);
    }

    const observables: Observable<unknown>[] = [];
    for (const key of ['policy', 'application'] as const) {
      const file = files[key];
      if (!file) {
        continue;
      }
      const url = insurance[key]?.uploadSignedUrl;
      observables.push(
        url
          ? this.uploadFile(file, url)
          : throwError(() => new Error(`The ${key} upload URL is missing.`))
      );
    }

    return observables.length === 0 ? of([]) : forkJoin(observables);
  }

  uploadFile(file: File, url: string) {
    return this.http.put(url, file);
  }

  /***
   *  Get insurance without specifying the customer
   *  */

  getInsuranceDetails(insuranceId: number): Observable<Insurance> {
    return this.http.get<Insurance>(`insurances/${insuranceId}`);
  }

  getInsuranceDamages(insuranceId: number): Observable<Damage[]> {
    return this.http.get<Damage[]>(`${this.url}/${insuranceId}/damages`);
  }

  getEmailTemplate(insuranceId: number) {
    return this.http.get<string>(`${this.url}/${insuranceId}/email-template`);
  }

  sendEmail(email: Email) {
    return this.http.post('smtp/send', email);
  }

  getAvailableModes(insuranceId: number) {
    return this.http.get<InsuranceMode[]>(`${this.url}/${insuranceId}/modes`);
  }

  mrMoney(insurance: Insurance) {
    return this.http
      .get(`${this.url}/${insurance.id}/mrmoney`, { responseType: 'text' })
      .pipe(tap(url => open(url, '_blank')));
  }

  searchForCommissionRawDataTable$(search: string): Observable<Insurance[]> {
    const queryParams = {
      search
    };
    if (search)
      return this.http
        .get<PaginationResponse<Insurance>>(`insurances`, {
          params: queryParams
        })
        .pipe(map(paginatedInsurances => paginatedInsurances.results));

    return this.http
      .get<PaginationResponse<Insurance>>(`insurances`)
      .pipe(map(paginatedInsurances => paginatedInsurances.results));
  }
}
